import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiBell,
  FiCheckCircle,
  FiClock,
  FiUser,
  FiTruck,
  FiCalendar,
  FiUserCheck,
} from 'react-icons/fi';
import { useNotificationStore, useAuthStore, useShopStore } from '../../store';
import './NotificationBell.css';

const NotificationBell = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const bellRef = useRef(null);

  const { user } = useAuthStore();
  const { selectedShop } = useShopStore();
  const {
    notifications,
    unreadCount,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    switchRoom,
  } = useNotificationStore();

  // Determine the shopId to use
  const shopId = user?.role === 'superadmin' ? selectedShop?._id : user?.shop;

  // La conexion del socket la abre Layout.jsx (para todos los roles);
  // aqui solo se consume el estado y se cambia de sala al cambiar de taller.

  // Switch room when superadmin changes shop
  useEffect(() => {
    if (shopId && user?.role === 'superadmin') {
      switchRoom(shopId);
    }
  }, [shopId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        bellRef.current &&
        !bellRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleBellClick = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      fetchNotifications();
    }
  };

  const handleNotificationClick = async (notification) => {
    if (!notification.isRead) {
      await markAsRead(notification._id);
    }

    // Navigate to calendar for appointment notifications
    if (notification.type === 'new_appointment' && notification.data?.workOrderId) {
      navigate('/calendar');
    } else if (notification.type === 'assignment' && notification.data?.workOrderId) {
      navigate(`/workorders/${notification.data.workOrderId}`);
    }

    setIsOpen(false);
  };

  const handleMarkAllRead = async () => {
    await markAllAsRead();
  };

  const formatTimeAgo = (date) => {
    const now = new Date();
    const notificationDate = new Date(date);
    const diffInSeconds = Math.floor((now - notificationDate) / 1000);

    if (diffInSeconds < 60) return 'Ahora mismo';
    if (diffInSeconds < 3600) return `Hace ${Math.floor(diffInSeconds / 60)} min`;
    if (diffInSeconds < 86400) return `Hace ${Math.floor(diffInSeconds / 3600)} h`;
    if (diffInSeconds < 604800) return `Hace ${Math.floor(diffInSeconds / 86400)} días`;
    return notificationDate.toLocaleDateString('es-SV');
  };

  // Todos los roles con sesión: el mecánico recibe avisos dirigidos a él
  // (ej. cuando otro toma una de sus órdenes).
  if (!user) {
    return null;
  }

  return (
    <div className="notification-bell-container">
      <button
        ref={bellRef}
        className={`notification-bell-btn ${unreadCount > 0 ? 'has-notifications' : ''}`}
        onClick={handleBellClick}
        title="Notificaciones"
      >
        <FiBell />
        {unreadCount > 0 && (
          <span className="notification-badge">{unreadCount > 9 ? '9+' : unreadCount}</span>
        )}
      </button>

      {isOpen && (
        <div ref={dropdownRef} className="notification-dropdown">
          <div className="notification-header">
            <h3>Notificaciones</h3>
            {unreadCount > 0 && (
              <button
                className="mark-all-read-btn"
                onClick={handleMarkAllRead}
                title="Marcar todas como leídas"
              >
                <FiCheckCircle />
                Marcar todas
              </button>
            )}
          </div>

          <div className="notification-list">
            {notifications.length === 0 ? (
              <div className="notification-empty">
                <FiBell />
                <p>No hay notificaciones</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification._id}
                  className={`notification-item ${!notification.isRead ? 'unread' : ''}`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="notification-icon">
                    {notification.type === 'new_appointment' ? (
                      <FiCalendar />
                    ) : notification.type === 'assignment' ? (
                      <FiUserCheck />
                    ) : (
                      <FiBell />
                    )}
                  </div>
                  <div className="notification-content">
                    <p className="notification-title">{notification.title}</p>
                    <p className="notification-message">{notification.message}</p>
                    {notification.data && (
                      <div className="notification-details">
                        {notification.data.customerName && (
                          <span>
                            <FiUser /> {notification.data.customerName}
                          </span>
                        )}
                        {notification.data.vehicleInfo && (
                          <span>
                            <FiTruck /> {notification.data.vehicleInfo}
                          </span>
                        )}
                        {notification.data.promisedDate && (
                          <span>
                            <FiClock />{' '}
                            {new Date(notification.data.promisedDate).toLocaleDateString('es-SV', {
                              day: 'numeric',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </span>
                        )}
                      </div>
                    )}
                    <span className="notification-time">
                      {formatTimeAgo(notification.createdAt)}
                    </span>
                  </div>
                  {!notification.isRead && <div className="notification-unread-dot" />}
                </div>
              ))
            )}
          </div>

          {notifications.length > 0 && ['admin', 'superadmin'].includes(user.role) && (
            <div className="notification-footer">
              <button onClick={() => navigate('/calendar')} className="view-all-btn">
                Ver calendario de citas
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
